import {
    Analytics as AnalyticsIcon,
    ShowChart as ChartIcon,
    Extension as CustomIcon,
    Storage as DataIcon,
    ExpandMore as ExpandMoreIcon,
    AccountBalance as PortfolioIcon,
} from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    alpha,
    Box,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Typography,
    useTheme,
} from '@mui/material';
import React from 'react';
import { useDrag } from 'react-dnd';

import { WidgetDefinition } from '../types/index.js';
import { widgetRegistry } from '../utils/widgetRegistry.js';

const categoryIcons = {
  market: ChartIcon,
  analysis: AnalyticsIcon,
  portfolio: PortfolioIcon,
  data: DataIcon,
  custom: CustomIcon,
};

interface DraggedWidget {
  type: string;
  config: any;
  size: { width: number; height: number };
}

interface WidgetPaletteItemProps {
  widget: WidgetDefinition;
}

const WidgetPaletteItem: React.FC<WidgetPaletteItemProps> = ({ widget }) => {
  const theme = useTheme();
  const Icon = widget.icon;

  const [{ isDragging }, drag] = useDrag<DraggedWidget, unknown, { isDragging: boolean }>({
    type: 'widget',
    item: {
      type: widget.type,
      config: widget.defaultConfig,
      size: widget.defaultSize,
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  return (
    <ListItem
      ref={drag}
      sx={{
        cursor: 'grab',
        opacity: isDragging ? 0.5 : 1,
        transition: 'all 0.2s',
        borderRadius: 1,
        mb: 0.5,
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
        },
        '&:active': {
          cursor: 'grabbing',
        },
      }}
    >
      <ListItemIcon>
        <Box component={Icon} sx={{ color: theme.palette.primary.main }} />
      </ListItemIcon>
      <ListItemText
        primary={widget.name}
        secondary={widget.description}
        primaryTypographyProps={{ variant: 'body2' }}
        secondaryTypographyProps={{ variant: 'caption' }}
      />
    </ListItem>
  );
};

export const WidgetPalette: React.FC = () => {
  const theme = useTheme();
  const [expanded, setExpanded] = React.useState<string | false>('market');
  
  // Get widgets from registry
  const allWidgets = widgetRegistry.list();

  // Group widgets by category
  const widgetsByCategory = allWidgets.reduce((acc, widget) => {
    if (!acc[widget.category]) {
      acc[widget.category] = [];
    }
    acc[widget.category].push(widget);
    return acc;
  }, {} as Record<string, WidgetDefinition[]>);

  const handleAccordionChange = (panel: string) => (
    _event: React.SyntheticEvent,
    isExpanded: boolean
  ) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Paper
      sx={{
        width: 280,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 0,
        borderRight: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
        background: `linear-gradient(180deg, ${alpha(theme.palette.background.default, 0.6)} 0%, ${alpha('#0A0A0A', 0.6)} 100%)`,
        backdropFilter: 'blur(10px) saturate(140%)',
      }}
    >
      <Box sx={{ p: 2, borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.08)}` }}>
        <Typography variant="h6" sx={{ letterSpacing: 0.3 }}>Widget Palette</Typography>
        <Typography variant="caption" color="text.secondary">
          Drag widgets to the canvas
        </Typography>
      </Box>

      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 1 }}>
        {Object.entries(widgetsByCategory)
          .filter(([category]) => category !== 'system') // Hide system widgets from palette
          .map(([category, widgets]) => {
          const CategoryIcon = categoryIcons[category as keyof typeof categoryIcons];
          
          return (
            <Accordion
              key={category}
              expanded={expanded === category}
              onChange={handleAccordionChange(category)}
              sx={{
                '&:before': { display: 'none' },
                boxShadow: 'none',
                border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
                borderRadius: 1,
                mb: 1,
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  '& .MuiAccordionSummary-content': { alignItems: 'center' },
                  background: (theme) => alpha(theme.palette.primary.main, 0.04),
                }}
              >
                <CategoryIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                <Typography variant="subtitle2" textTransform="capitalize">
                  {category}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 1 }}>
                <List disablePadding>
                  {widgets.map((widget) => (
                    <WidgetPaletteItem key={widget.type} widget={widget} />
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </Paper>
  );
};